import { useCallback, useEffect, useState } from "react";
import type { AuthAvailabilityState } from "./types";

interface AuthStartupOverlayProps {
  state: AuthAvailabilityState;
}

/**
 * Full-page overlay shown while the authentication service is unavailable or being initialized.
 * Displays a countdown timer and "Try Again Now" button for manual retry.
 */
export function AuthStartupOverlay({ state }: AuthStartupOverlayProps) {
  const isRetrying = state.status === "retrying";
  const [displaySeconds, setDisplaySeconds] = useState<number | null>(
    state.nextRetryDelayMs ? Math.ceil(state.nextRetryDelayMs / 1_000) : null,
  );

  // Update countdown timer every second
  useEffect(() => {
    if (!isRetrying || !state.nextRetryDelayMs) {
      return;
    }

    setDisplaySeconds(Math.ceil(state.nextRetryDelayMs / 1_000));

    const interval = window.setInterval(() => {
      setDisplaySeconds((current) => {
        if (current === null || current <= 1) {
          return null;
        }
        return current - 1;
      });
    }, 1_000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isRetrying, state.nextRetryDelayMs]);

  const handleRetryNow = useCallback(() => {
    // Trigger a probe immediately by dispatching an event
    window.dispatchEvent(new CustomEvent("auth:retry-now"));
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-bg/85 px-6 backdrop-blur-md">
      <div className="w-full max-w-xl rounded-2xl border border-border bg-overlay/95 p-8 shadow-2xl">
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-fg">
              Sign-in unavailable
            </p>
            <h1 className="text-2xl font-semibold text-fg">
              {isRetrying
                ? "We're having trouble connecting"
                : "Setting up authentication"}
            </h1>
          </div>

          {isRetrying ? (
            <>
              <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger-subtle-fg">
                <p className="font-medium text-danger">{state.errorMessage}</p>
                <p className="mt-2 text-muted-fg">
                  We're automatically retrying to reconnect. This usually resolves
                  itself within a few minutes.
                </p>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border/70 bg-bg/70 px-4 py-3 text-sm text-muted-fg">
                <span>Attempt {state.failureCount + 1}</span>
                <span>
                  {displaySeconds
                    ? `Retrying in ${displaySeconds}s`
                    : "Checking now…"}
                </span>
              </div>
              <button
                onClick={handleRetryNow}
                className="w-full rounded-lg border border-primary bg-primary px-4 py-2.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary/90 active:bg-primary/80"
              >
                Try Again Now
              </button>
            </>
          ) : (
            <p className="text-sm text-muted-fg">
              We're getting your sign-in service ready. Just a moment…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
